const DEFAULT_POLL_SECONDS = 60;
const DEFAULT_MAX_CONCURRENT = 2;

function namedConfigs(settings) {
  const configs = Array.isArray(settings.configs) ? settings.configs : [];

  return configs.filter((cfg) => {
    if (!cfg || typeof cfg !== 'object') {
      return false;
    }
    const name = typeof cfg.name === 'string' ? cfg.name.trim() : '';

    return name.length > 0;
  });
}

function hasReceivedMappingRows(settings) {
  const received = Array.isArray(settings.received) ? settings.received : [];

  return received.some((row) => {
    if (!row || typeof row !== 'object') {
      return false;
    }
    const tin = typeof row.tin === 'string' ? row.tin.trim() : '';
    const format = typeof row.file_format === 'string' ? row.file_format.trim().toLowerCase() : '';

    return tin.length > 0 && (format === 'json' || format === 'xlsx');
  });
}

function derivePollAndConcurrency(settings) {
  let poll = Number(settings.poll_interval_seconds);
  let concurrent = Number(settings.max_concurrent_files);
  if (!Number.isFinite(concurrent) || concurrent <= 0) {
    concurrent = 0;
  }

  for (const cfg of namedConfigs(settings)) {
    const rowPoll = Number(cfg.poll_interval_seconds);
    if (rowPoll > 0) {
      if (!Number.isFinite(poll) || poll <= 0 || rowPoll < poll) {
        poll = rowPoll;
      }
    }
    const rowConcurrent = Number(cfg.max_concurrent_files);
    if (Number.isFinite(rowConcurrent) && rowConcurrent > concurrent) {
      concurrent = rowConcurrent;
    }
  }

  if (!Number.isFinite(poll) || poll <= 0) {
    poll = DEFAULT_POLL_SECONDS;
  }
  if (!Number.isFinite(concurrent) || concurrent <= 0) {
    concurrent = DEFAULT_MAX_CONCURRENT;
  }

  return { poll_interval_seconds: poll, max_concurrent_files: concurrent };
}

/** Align with auth-service NormalizeSftpSettings. */
export function normalizeSftpSettings(settings) {
  if (!settings || typeof settings !== 'object') {
    return {
      enabled: false,
      poll_interval_seconds: DEFAULT_POLL_SECONDS,
      max_concurrent_files: DEFAULT_MAX_CONCURRENT,
      received: [],
    };
  }

  const { poll_interval_seconds, max_concurrent_files } = derivePollAndConcurrency(settings);
  const hasRunnableConfig = namedConfigs(settings).length > 0 || hasReceivedMappingRows(settings);
  const explicitEnabled = typeof settings.enabled === 'boolean';
  const enabled = explicitEnabled ? settings.enabled : hasRunnableConfig;

  const received = normalizeReceivedRows(settings.received);

  return {
    enabled,
    poll_interval_seconds,
    max_concurrent_files,
    received,
  };
}

const RECEIVED_DOC_TYPES = ['INV', 'CN', 'SBINV', 'SBCN'];

function trimText(value) {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
}

function normalizeConnectorMap(rawConnectors) {
  const connectors = {};
  const source = rawConnectors && typeof rawConnectors === 'object' ? rawConnectors : {};
  for (const [key, value] of Object.entries(source)) {
    const bucket = trimText(key).toUpperCase();
    const connectorKey = trimText(value);
    if (RECEIVED_DOC_TYPES.includes(bucket) && connectorKey) {
      connectors[bucket] = connectorKey;
    }
  }

  return connectors;
}

function normalizeReceivedRows(received) {
  if (!Array.isArray(received)) {
    return [];
  }

  return received
    .map((row) => ({
      tin: trimText(row?.tin),
      file_format: typeof row?.file_format === 'string' ? row.file_format.trim().toLowerCase() : 'json',
      connectors: normalizeConnectorMap(row?.connectors),
    }))
    .filter((row) => row.tin && row.file_format);
}
